import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { AlertTriangleIcon, CheckIcon, CalendarIcon, ClockIcon, XIcon, FileTextIcon, InfoIcon } from 'lucide-react';
export function SickReport() {
  const [reportStep, setReportStep] = useState<'form' | 'confirmation' | 'success'>('form');
  const [formData, setFormData] = useState({
    startDate: new Date().toISOString().split('T')[0],
    expectedDuration: '1',
    reason: '',
    notes: '',
    contactPhone: '',
    notifyClients: true,
    cancelTrainings: false
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  // Mock data for upcoming trainings
  const upcomingTrainings = [{
    id: 1,
    date: new Date(new Date().setDate(new Date().getDate() + 1)),
    startTime: new Date(new Date().setDate(new Date().getDate() + 1)).setHours(9, 0),
    endTime: new Date(new Date().setDate(new Date().getDate() + 1)).setHours(12, 0),
    type: 'Sliptraining Basis',
    location: 'Rotterdam',
    client: 'Gemeente Rotterdam',
    participants: 8
  }, {
    id: 2,
    date: new Date(new Date().setDate(new Date().getDate() + 3)),
    startTime: new Date(new Date().setDate(new Date().getDate() + 3)).setHours(13, 30),
    endTime: new Date(new Date().setDate(new Date().getDate() + 3)).setHours(16, 30),
    type: 'Defensief Rijden',
    location: 'Utrecht',
    client: 'ProRail',
    participants: 6
  }];
  // Handle form input changes
  const handleInputChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    // Clear validation error when field is changed
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: ''
      });
    }
  };
  // Validate form
  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.startDate) {
      errors.startDate = 'Startdatum is verplicht';
    }
    if (!formData.expectedDuration) {
      errors.expectedDuration = 'Verwachte duur is verplicht';
    }
    if (!formData.contactPhone && formData.contactPhone.length < 10) {
      errors.contactPhone = 'Vul een geldig telefoonnummer in';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm()) {
      setReportStep('confirmation');
    }
  };
  // Handle confirmation
  const handleConfirm = () => {
    // In a real app, this would send the data to the server
    setReportStep('success');
  };
  // Handle back button
  const handleBack = () => {
    setReportStep('form');
  };
  // Handle new report
  const handleNewReport = () => {
    setFormData({
      startDate: new Date().toISOString().split('T')[0],
      expectedDuration: '1',
      reason: '',
      notes: '',
      contactPhone: '',
      notifyClients: true,
      cancelTrainings: false
    });
    setReportStep('form');
  };
  // Format date
  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  // Format time
  const formatTime = date => {
    return new Date(date).toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Calculate end date based on start date and duration
  const calculateEndDate = () => {
    const startDate = new Date(formData.startDate);
    const durationDays = parseInt(formData.expectedDuration);
    if (isNaN(durationDays) || durationDays === 0 || formData.expectedDuration === 'unknown') {
      return 'Onbekend';
    }
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + durationDays - 1); // -1 because start day counts as day 1
    return formatDate(endDate);
  };
  // Render form step
  const renderFormStep = () => {
    return <form onSubmit={handleSubmit} data-id="element-1621">
        <div className="space-y-4" data-id="element-1622">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1623">
            <div data-id="element-1624">
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1625">
                Startdatum ziekmelding*
              </label>
              <input type="date" id="startDate" name="startDate" value={formData.startDate} onChange={handleInputChange} className={`block w-full px-3 py-2 border ${validationErrors.startDate ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-[#B61C1C] focus:border-[#B61C1C] sm:text-sm`} min={new Date().toISOString().split('T')[0]} data-id="element-1626" />
              {validationErrors.startDate && <p className="mt-1 text-sm text-red-600" data-id="element-1627">
                  {validationErrors.startDate}
                </p>}
            </div>
            <div data-id="element-1628">
              <label htmlFor="expectedDuration" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1629">
                Verwachte duur*
              </label>
              <select id="expectedDuration" name="expectedDuration" value={formData.expectedDuration} onChange={handleInputChange} className={`block w-full px-3 py-2 border ${validationErrors.expectedDuration ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-[#B61C1C] focus:border-[#B61C1C] sm:text-sm`} data-id="element-1630">
                <option value="1" data-id="element-1631">1 dag</option>
                <option value="2" data-id="element-1632">2 dagen</option>
                <option value="3" data-id="element-1633">3 dagen</option>
                <option value="4" data-id="element-1634">4 dagen</option>
                <option value="5" data-id="element-1635">5 dagen</option>
                <option value="7" data-id="element-1636">1 week</option>
                <option value="14" data-id="element-1637">2 weken</option>
                <option value="unknown" data-id="element-1638">Onbekend</option>
              </select>
              {validationErrors.expectedDuration && <p className="mt-1 text-sm text-red-600" data-id="element-1639">
                  {validationErrors.expectedDuration}
                </p>}
            </div>
          </div>
          <div data-id="element-1640">
            <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1641">
              Reden (optioneel)
            </label>
            <select id="reason" name="reason" value={formData.reason} onChange={handleInputChange} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#B61C1C] focus:border-[#B61C1C] sm:text-sm" data-id="element-1642">
              <option value="" data-id="element-1643">Selecteer een reden (optioneel)</option>
              <option value="ziekte" data-id="element-1644">Ziekte</option>
              <option value="griep" data-id="element-1645">Griep / Verkoudheid</option>
              <option value="corona" data-id="element-1646">Corona / COVID-19</option>
              <option value="ongeval" data-id="element-1647">Ongeval</option>
              <option value="operatie" data-id="element-1648">Operatie / Medische ingreep</option>
              <option value="overig" data-id="element-1649">Overig</option>
            </select>
          </div>
          <div data-id="element-1650">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1651">
              Toelichting (optioneel)
            </label>
            <textarea id="notes" name="notes" rows={3} value={formData.notes} onChange={handleInputChange} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#B61C1C] focus:border-[#B61C1C] sm:text-sm" placeholder="Eventuele toelichting of bijzonderheden..." data-id="element-1652"></textarea>
          </div>
          <div data-id="element-1653">
            <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1654">
              Telefoonnummer waarop je bereikbaar bent
            </label>
            <input type="tel" id="contactPhone" name="contactPhone" value={formData.contactPhone} onChange={handleInputChange} className={`block w-full px-3 py-2 border ${validationErrors.contactPhone ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-[#B61C1C] focus:border-[#B61C1C] sm:text-sm`} placeholder="06-12345678" data-id="element-1655" />
            {validationErrors.contactPhone && <p className="mt-1 text-sm text-red-600" data-id="element-1656">
                {validationErrors.contactPhone}
              </p>}
          </div>
          <div className="space-y-2" data-id="element-1657">
            <div className="flex items-center" data-id="element-1658">
              <input type="checkbox" id="notifyClients" name="notifyClients" checked={formData.notifyClients} onChange={handleInputChange} className="h-4 w-4 text-[#B61C1C] focus:ring-[#B61C1C] border-gray-300 rounded" data-id="element-1659" />
              <label htmlFor="notifyClients" className="ml-2 block text-sm text-gray-700" data-id="element-1660">
                Informeer klanten over mijn afwezigheid
              </label>
            </div>
            <div className="flex items-center" data-id="element-1661">
              <input type="checkbox" id="cancelTrainings" name="cancelTrainings" checked={formData.cancelTrainings} onChange={handleInputChange} className="h-4 w-4 text-[#B61C1C] focus:ring-[#B61C1C] border-gray-300 rounded" data-id="element-1662" />
              <label htmlFor="cancelTrainings" className="ml-2 block text-sm text-gray-700" data-id="element-1663">
                Annuleer mijn geplande trainingen
              </label>
            </div>
          </div>
          {upcomingTrainings.length > 0 && <div className="mt-4 p-4 bg-yellow-50 border border-yellow-100 rounded-md" data-id="element-1664">
              <div className="flex items-start" data-id="element-1665">
                <AlertTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-1666" />
                <div data-id="element-1667">
                  <h4 className="text-sm font-medium text-yellow-800" data-id="element-1668">
                    Let op: Je hebt geplande trainingen
                  </h4>
                  <p className="text-xs text-yellow-700 mt-1" data-id="element-1669">
                    Je hebt {upcomingTrainings.length} trainingen gepland in de
                    komende dagen.
                    {formData.cancelTrainings ? ' Deze zullen worden geannuleerd.' : ' De planner zal een vervangende instructeur zoeken.'}
                  </p>
                  <ul className="mt-2 space-y-1" data-id="element-1670">
                    {upcomingTrainings.map(training => <li key={training.id} className="text-xs text-yellow-700 flex items-center" data-id="element-1671">
                        <CalendarIcon className="h-3 w-3 mr-1" data-id="element-1672" />
                        <span data-id="element-1673">
                          {formatDate(training.date)} |{' '}
                          {formatTime(training.startTime)} | {training.type} |{' '}
                          {training.location}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>}
        </div>
        <div className="mt-6 flex justify-end" data-id="element-1674">
          <Button type="submit" variant="primary" data-id="element-1675">
            <AlertTriangleIcon className="h-4 w-4 mr-2" data-id="element-1676" />
            Ziek Melden
          </Button>
        </div>
      </form>;
  };
  // Render confirmation step
  const renderConfirmationStep = () => {
    return <div className="space-y-4" data-id="element-1677">
        <div className="p-4 bg-yellow-50 border border-yellow-100 rounded-md" data-id="element-1678">
          <div className="flex items-start" data-id="element-1679">
            <AlertTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-1680" />
            <div data-id="element-1681">
              <h4 className="text-sm font-medium text-yellow-800" data-id="element-1682">
                Bevestig je ziekmelding
              </h4>
              <p className="text-xs text-yellow-700 mt-1" data-id="element-1683">
                Controleer onderstaande gegevens voordat je je ziekmelding
                bevestigt.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-md" data-id="element-1684">
          <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1685">
            Ziekmelding details
          </h4>
          <div className="space-y-2" data-id="element-1686">
            <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-1687">
              <div className="text-gray-500" data-id="element-1688">Startdatum:</div>
              <div className="font-medium" data-id="element-1689">
                {formatDate(formData.startDate)}
              </div>
              <div className="text-gray-500" data-id="element-1690">Verwachte einddatum:</div>
              <div className="font-medium" data-id="element-1691">{calculateEndDate()}</div>
              {formData.reason && <>
                  <div className="text-gray-500" data-id="element-1692">Reden:</div>
                  <div className="font-medium" data-id="element-1693">{formData.reason}</div>
                </>}
              <div className="text-gray-500" data-id="element-1694">Contact telefoon:</div>
              <div className="font-medium" data-id="element-1695">
                {formData.contactPhone || 'Niet opgegeven'}
              </div>
              <div className="text-gray-500" data-id="element-1696">Klanten informeren:</div>
              <div className="font-medium" data-id="element-1697">
                {formData.notifyClients ? 'Ja' : 'Nee'}
              </div>
              <div className="text-gray-500" data-id="element-1698">Trainingen annuleren:</div>
              <div className="font-medium" data-id="element-1699">
                {formData.cancelTrainings ? 'Ja' : 'Nee'}
              </div>
            </div>
            {formData.notes && <div className="mt-2" data-id="element-1700">
                <div className="text-gray-500 text-sm" data-id="element-1701">Toelichting:</div>
                <div className="text-sm mt-1 p-2 bg-white rounded border border-gray-200" data-id="element-1702">
                  {formData.notes}
                </div>
              </div>}
          </div>
        </div>
        {upcomingTrainings.length > 0 && <div data-id="element-1703">
            <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-1704">
              Geplande trainingen tijdens ziekteperiode
            </h4>
            <div className="space-y-2" data-id="element-1705">
              {upcomingTrainings.map(training => <div key={training.id} className="p-3 bg-white border border-gray-200 rounded-md" data-id="element-1706">
                  <div className="flex justify-between items-start" data-id="element-1707">
                    <div data-id="element-1708">
                      <h5 className="text-sm font-medium text-gray-900" data-id="element-1709">
                        {training.type}
                      </h5>
                      <p className="text-xs text-gray-500" data-id="element-1710">{training.client}</p>
                      <div className="flex items-center mt-1 text-xs text-gray-500" data-id="element-1711">
                        <CalendarIcon className="h-3 w-3 mr-1" data-id="element-1712" />
                        <span data-id="element-1713">{formatDate(training.date)}</span>
                      </div>
                      <div className="flex items-center mt-1 text-xs text-gray-500" data-id="element-1714">
                        <ClockIcon className="h-3 w-3 mr-1" data-id="element-1715" />
                        <span data-id="element-1716">
                          {formatTime(training.startTime)} -{' '}
                          {formatTime(training.endTime)}
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-red-600" data-id="element-1717">
                      {formData.cancelTrainings ? 'Wordt geannuleerd' : 'Vervanging nodig'}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>}
        <div className="mt-6 flex justify-between" data-id="element-1718">
          <Button variant="outline" onClick={handleBack} data-id="element-1719">
            <XIcon className="h-4 w-4 mr-2" data-id="element-1720" />
            Terug
          </Button>
          <Button variant="primary" onClick={handleConfirm} data-id="element-1721">
            <CheckIcon className="h-4 w-4 mr-2" data-id="element-1722" />
            Bevestig Ziekmelding
          </Button>
        </div>
      </div>;
  };
  // Render success step
  const renderSuccessStep = () => {
    return <div className="text-center py-6" data-id="element-1723">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100" data-id="element-1724">
          <CheckIcon className="h-6 w-6 text-green-600" data-id="element-1725" />
        </div>
        <h3 className="mt-3 text-lg font-medium text-gray-900" data-id="element-1726">
          Ziekmelding Succesvol
        </h3>
        <p className="mt-2 text-sm text-gray-500" data-id="element-1727">
          Je ziekmelding is succesvol doorgegeven. Beterschap!
        </p>
        <div className="mt-6" data-id="element-1728">
          <div className="bg-gray-50 p-4 rounded-md text-left" data-id="element-1729">
            <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1730">
              Samenvatting
            </h4>
            <div className="space-y-2" data-id="element-1731">
              <div className="grid grid-cols-2 gap-2 text-sm" data-id="element-1732">
                <div className="text-gray-500" data-id="element-1733">Startdatum:</div>
                <div className="font-medium" data-id="element-1734">
                  {formatDate(formData.startDate)}
                </div>
                <div className="text-gray-500" data-id="element-1735">Verwachte einddatum:</div>
                <div className="font-medium" data-id="element-1736">{calculateEndDate()}</div>
              </div>
            </div>
          </div>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-md text-left" data-id="element-1737">
            <div className="flex" data-id="element-1738">
              <InfoIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-1739" />
              <div data-id="element-1740">
                <h4 className="text-sm font-medium text-blue-800" data-id="element-1741">Wat nu?</h4>
                <p className="text-xs text-blue-700 mt-1" data-id="element-1742">
                  De planningsafdeling is op de hoogte gebracht van je
                  ziekmelding. Je hoeft verder niets te doen.
                </p>
                <p className="text-xs text-blue-700 mt-1" data-id="element-1743">
                  Wanneer je weer beter bent, kun je je beter melden via het
                  dashboard.
                </p>
                <p className="text-xs text-blue-700 mt-1" data-id="element-1744">
                  Voor vragen kun je contact opnemen met de planningsafdeling.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6" data-id="element-1745">
          <Button variant="outline" onClick={handleNewReport} data-id="element-1746">
            <FileTextIcon className="h-4 w-4 mr-2" data-id="element-1747" />
            Nieuwe Ziekmelding
          </Button>
        </div>
      </div>;
  };
  return <div className="space-y-6" data-id="element-1748">
      <div className="flex justify-between items-center" data-id="element-1749">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1750">Ziek Melden</h1>
      </div>
      <Card data-id="element-1751">
        <div className="p-4 border-b border-gray-200" data-id="element-1752">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-1753">
            {reportStep === 'form' && 'Ziekmelding Formulier'}
            {reportStep === 'confirmation' && 'Bevestig Ziekmelding'}
            {reportStep === 'success' && 'Ziekmelding Bevestigd'}
          </h2>
        </div>
        <div className="p-6" data-id="element-1754">
          {reportStep === 'form' && renderFormStep()}
          {reportStep === 'confirmation' && renderConfirmationStep()}
          {reportStep === 'success' && renderSuccessStep()}
        </div>
      </Card>
    </div>;
}